import React from "react";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "./SubscriptionGate";
import { Button } from "./ui/button";
import { Crown, Sparkles, ArrowLeft, Lock, Check, Gem } from "lucide-react";

const FREE_FEATURES = [
  "Daily mood questionnaire",
  "1 AI training plan per week",
  "2 brain games (Easy mode)",
  "Basic streak tracking",
  "2 AI guided meditation trials",
];

const PRO_FEATURES = [
  "Unlimited daily AI training plans",
  "3 AI meditations per day",
  "All 5 brain games, all difficulties",
  "Workout Lab & custom exercises",
  "Weekly AI reports & digest",
  "Leaderboard ranking",
  "Shareable profile card",
];

const DIAMOND_FEATURES = [
  "Everything in Pro",
  "AI Sleep Stories (1 per day)",
  "Deep Dive clinical assessments",
  "Advanced AI mood insights",
  "Premium voice selection",
];

export const PremiumPageGuard = ({ children, feature, requiredTier = "pro" }) => {
  const { isPro, isDiamond, tier } = useSubscription();
  const navigate = useNavigate();

  const needsDiamond = requiredTier === "diamond";
  const hasAccess = needsDiamond ? isDiamond : isPro;

  // While tier is loading, show content (avoid flash of paywall)
  if (tier === null || hasAccess) return children;

  return (
    <div className="min-h-screen bg-sky-50 flex items-center justify-center p-4" data-testid="premium-guard">
      <div className="max-w-md w-full text-center">
        <div className="glass-card p-8">
          <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 ${needsDiamond ? "bg-violet-100" : "bg-amber-100"}`}>
            {needsDiamond ? <Gem className="w-10 h-10 text-violet-500" /> : <Crown className="w-10 h-10 text-amber-500" />}
          </div>
          <h2 className="text-2xl font-light text-sky-900 mb-3" style={{ fontFamily: "Outfit" }}>
            {needsDiamond ? "Diamond Feature" : "Pro Feature"}
          </h2>
          <p className="text-slate-500 text-sm mb-6">
            {feature} is available with LumiFit {needsDiamond ? "Diamond" : "Pro"}.{" "}
            {needsDiamond ? "Unlock the complete experience." : "Start your 7-day free trial today."}
          </p>

          <div className="text-left space-y-4 mb-6">
            {!needsDiamond && (
              <>
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Free</p>
                  {FREE_FEATURES.map((f) => (
                    <div key={f} className="flex items-center gap-2 text-sm text-slate-500 py-0.5">
                      <Check className="w-3.5 h-3.5 text-slate-300" />
                      {f}
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-xs text-amber-500 uppercase tracking-wider mb-2">Pro</p>
                  {PRO_FEATURES.map((f) => (
                    <div key={f} className="flex items-center gap-2 text-sm text-sky-700 py-0.5">
                      <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                      {f}
                    </div>
                  ))}
                </div>
              </>
            )}
            {needsDiamond && (
              <div>
                <p className="text-xs text-violet-500 uppercase tracking-wider mb-2">Diamond</p>
                {DIAMOND_FEATURES.map((f) => (
                  <div key={f} className="flex items-center gap-2 text-sm text-violet-700 py-0.5">
                    <Gem className="w-3.5 h-3.5 text-violet-400" />
                    {f}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Button
              onClick={() => navigate("/subscription")}
              className={`w-full py-3 ${needsDiamond ? "bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-600 hover:to-indigo-600 text-white" : "glow-button"}`}
              data-testid="upgrade-to-pro-btn"
            >
              {needsDiamond ? <><Gem className="w-4 h-4 mr-2" /> Upgrade to Diamond</> : <><Crown className="w-4 h-4 mr-2" /> Upgrade to Pro</>}
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate("/dashboard")}
              className="w-full text-slate-400"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
